import React, { Component } from "react";
import { isEmpty, findIndex } from "lodash";
import { connect } from "react-redux";
import { NavLink } from "react-router-dom";
import { Skeleton, List, Row, Col, Divider } from "antd";

import EXTERNAL_LINKS from "utils/externalLinks";
import EmailModal from "components/email/EmailModal";

import {
  makeSubjectErrorOb,
  makeFailureMessages,
  renderFailureMessages,
} from "utils/helpers";

import SubjectItem from "./SubjectItem";
import ApiErrorHandling from "./ApiErrorHandling";
import TableTopPopOver from "./pop-overs/TableTopPopOver";
import MasterButton, {
  BUTTON_TYPES,
  BackButton,
} from "components/ui/MasterButton";
import { ACTION_TYPE } from "reducers/actionType";

import "styles/AtarEligible.scss";

import IconExternalLink from "assets/Icon-External-Link.svg";
import IconWarning from "assets/Icon-Warning.svg";

const SUBJECT_NUM = {
  // FIRST CASE
  MODERN_HISTORY: 15270,
  ANCIENT_HISTORY: 15020,
  HISTORY_EXTENSION: 15280, //require if relevant

  // SECOND CASE
  PHYSICS: 15330,
  BIOLOGY: 15030,
  CHEMISTRY: 15050,
  EARTH_AND_ENVIRONMENTAL_SCIENCE: 15100,
  INVESTIGATING_SCIENCE: 15215,
  SCIENCE_EXTENSION: 15345,

  // THIRD CASE
  MATHEMATICS_ADVANCED: 15255,
  MATHEMATICS_EXTENSION_1: 15250,
  MATHEMATICS_EXTENSION_2: 15260,
};

const MIN_DISTINCT_SUBJECT_CODES = 4;

const TEMP_HISTORY_EXT_WARNING =
  "You must select Modern History (15270) or Ancient History (15020) before you can select History Extension (15280)";
const TEMP_SCIENCE_EXT_WARNING =
  "You must select Physics (15330), Biology (15030), Chemistry (15050), Earth & Environmental Science (15100) or Investigating Science (15215) before you can select Science Extension (15345).";

class AtarEligible extends Component {
  state = {
    modalVisible: false,
  };

  // Modal Functions
  showModal = () => this.setState({ modalVisible: true });
  closeModal = () => this.setState({ modalVisible: false });

  renderExternalLinks = () =>
    EXTERNAL_LINKS.map((link, i) => (
      <div key={i}>
        <a
          className="desktop-navbar-text"
          target="_blank"
          rel="noopener noreferrer"
          href={link.url}
        >
          <h2 className="alt-link">
            {link.displayName}
            <img
              src={IconExternalLink}
              alt="External Link to More Info"
              style={{ marginLeft: 10, marginBottom: 4 }}
            />
          </h2>
        </a>
        {i < EXTERNAL_LINKS.length - 1 && <Divider />}
      </div>
    ));

  renderBackScreen = () => {
    return (
      <div className="padding">
        <BackButton navlink="/summary" />
        <h1>Please choose between 1 - 15 subjects to check for eligibility</h1>
      </div>
    );
  };

  frontendErrorMessageFixing = () => {
    // SET UP
    const eligibility = { ...this.props.eligibility };
    const { subjects, details } = eligibility;
    if (!subjects || !details) {
      console.error("no eligibility:", eligibility);
      return;
    }

    const historyExtensionSubjectIndex = subjects.findIndex(
      (sub) => sub.subject_num === SUBJECT_NUM.HISTORY_EXTENSION
    );
    const scienceExtensionSubjectIndex = subjects.findIndex(
      (sub) => sub.subject_num === SUBJECT_NUM.SCIENCE_EXTENSION
    );

    // BASE REQUIREMENT RULE FIX --------------------------
    const baseRequirementRuleIndex = details.findIndex(
      (det) =>
        det.identifier === "all_base_subjects_present_requirement" &&
        det.status === "INDETERMINATE"
    );
    if (baseRequirementRuleIndex >= 0) {
      const baseRequirementRule = details[baseRequirementRuleIndex];

      // fix history case
      const historyExtensionCaseIndex = baseRequirementRule.messages.findIndex(
        (message) => message.cause === SUBJECT_NUM.HISTORY_EXTENSION
      );
      const historyExtensionCase =
        baseRequirementRule.messages[historyExtensionCaseIndex];
      if (historyExtensionCase) {
        const fulfilled = subjects.find(
          (sub) =>
            sub.subject_num === SUBJECT_NUM.MODERN_HISTORY ||
            sub.subject_num === SUBJECT_NUM.ANCIENT_HISTORY
        );
        if (fulfilled) {
          baseRequirementRule.messages.splice(historyExtensionCaseIndex, 1);
          if (isEmpty(baseRequirementRule.messages)) {
            baseRequirementRule.status = "ELIGIBLE";
          }
          details[baseRequirementRuleIndex] = baseRequirementRule;
          eligibility.details = details;
        }
        if (!fulfilled) {
          baseRequirementRule.messages[historyExtensionCaseIndex].message =
            TEMP_HISTORY_EXT_WARNING;
        }
      }

      // fix science extension case
      const scienceExtensionCaseIndex = baseRequirementRule.messages.findIndex(
        (message) => message.cause === SUBJECT_NUM.SCIENCE_EXTENSION
      );
      const scienceExtensionCase =
        baseRequirementRule.messages[scienceExtensionCaseIndex];
      if (scienceExtensionCase) {
        const fulfilled = subjects.find(
          (sub) =>
            sub.subject_num === SUBJECT_NUM.PHYSICS ||
            sub.subject_num === SUBJECT_NUM.BIOLOGY ||
            sub.subject_num === SUBJECT_NUM.CHEMISTRY ||
            sub.subject_num === SUBJECT_NUM.EARTH_AND_ENVIRONMENTAL_SCIENCE ||
            sub.subject_num === SUBJECT_NUM.INVESTIGATING_SCIENCE
        );
        if (fulfilled) {
          baseRequirementRule.messages.splice(scienceExtensionCaseIndex, 1);
          if (isEmpty(baseRequirementRule.messages)) {
            baseRequirementRule.status = "ELIGIBLE";
          }
          details[baseRequirementRuleIndex] = baseRequirementRule;
          eligibility.details = details;
        } else {
          baseRequirementRule.messages[scienceExtensionCaseIndex].message =
            TEMP_SCIENCE_EXT_WARNING;
        }
      }
    }

    // DISTINCT SUBJECT CODES RULE FIX --------------------------
    const distinctSubjectRuleIndex = details.findIndex(
      (det) =>
        det.identifier === "distinct_subject_codes_requirement" &&
        // if there appears to be no issue detected on the backend, this is when we do the frontend check.
        det.status === "ELIGIBLE"
    );
    if (
      distinctSubjectRuleIndex >= 0 &&
      (historyExtensionSubjectIndex > -1 || scienceExtensionSubjectIndex > -1)
    ) {
      const distinctSubjectRule = details[distinctSubjectRuleIndex];
      const filteredSubjects = [...subjects];
      const subjectCodesMap = {};

      // remove the subjects 'HISTORY EXTENSION' and 'SCIENCE EXTENSION' from the subject pool, because they do not count as disinct subjects
      if (historyExtensionSubjectIndex > -1) {
        filteredSubjects.splice(historyExtensionSubjectIndex, 1);
      }
      if (scienceExtensionSubjectIndex > -1) {
        filteredSubjects.splice(scienceExtensionSubjectIndex, 1);
      }

      filteredSubjects.forEach((sub) => {
        if (subjectCodesMap[sub.code]) {
          subjectCodesMap[sub.code] = [...subjectCodesMap[sub.code], sub];
        } else {
          subjectCodesMap[sub.code] = [sub];
        }
      });
      const distinctSubjectCodes = Object.keys(subjectCodesMap).length;

      if (distinctSubjectCodes < MIN_DISTINCT_SUBJECT_CODES) {
        distinctSubjectRule.messages = [
          {
            cause: null,
            message: `You have only selected ${distinctSubjectCodes} subjects. You need to select at least ${MIN_DISTINCT_SUBJECT_CODES}.`,
          },
        ];
        distinctSubjectRule.status = "INELIGIBLE";
        details[distinctSubjectRuleIndex] = distinctSubjectRule;
        eligibility.details = details;
        eligibility.status = "INELIGIBLE";
      }
    }

    // REMOVING CATEGORY A & B RULE FIX
    const categoryRuleIndex = details.findIndex(
      (det) =>
        det.identifier === "category_requirement" && det.status === "INELIGIBLE"
    );
    if (categoryRuleIndex >= 0) {
      const categoryRule = details[categoryRuleIndex];
      categoryRule.messages = [];
      categoryRule.status = "ELIGIBLE";
      details[categoryRuleIndex] = { ...categoryRule };
    }

    // MATHS EXTENSION II
    const MATHS_EXT2_PREREQ_MSG =
      "You must select Mathematics Extension 1 (15250) before you can select Mathematics Extension 2 (15260).";
    const hasMathsAdvanced =
      findIndex(
        subjects,
        (sub) => sub.subject_num === SUBJECT_NUM.MATHEMATICS_ADVANCED
      ) !== -1;
    const hasMathsExtI =
      findIndex(
        subjects,
        (sub) => sub.subject_num === SUBJECT_NUM.MATHEMATICS_EXTENSION_1
      ) !== -1;
    const hasMathsExtII =
      findIndex(
        subjects,
        (sub) => sub.subject_num === SUBJECT_NUM.MATHEMATICS_EXTENSION_2
      ) !== -1;

    if (!hasMathsAdvanced && !hasMathsExtI && hasMathsExtII) {
      const illegalCombinationsAbsentIndex = details.findIndex(
        (det) => det.identifier === "illegal_combinations_absent_requirement"
      );
      if (illegalCombinationsAbsentIndex !== -1) {
        const illegalCombinationsAbsent =
          details[illegalCombinationsAbsentIndex];
        const newMessage = {
          cause: SUBJECT_NUM.MATHEMATICS_EXTENSION_2,
          message: MATHS_EXT2_PREREQ_MSG,
          status: "INDETERMINATE",
        };
        illegalCombinationsAbsent.messages.push(newMessage);
        illegalCombinationsAbsent.status = "INELIGIBLE";
        details[illegalCombinationsAbsentIndex] = {
          ...illegalCombinationsAbsent,
        };
        eligibility.details = [...details];
      }
    }

    if (eligibility.details.every((det) => det.status === "ELIGIBLE")) {
      eligibility.status = "ELIGIBLE";
    } else {
      eligibility.status = "INELIGIBLE";
    }

    this.props.dispatch({
      type: ACTION_TYPE.eligibilityFetch.success,
      data: { eligibility },
    });
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.loading && !this.props.loading) {
      this.frontendErrorMessageFixing();
    }
  };

  render() {
    const { loading, eligibility, error } = this.props;

    if (error) {
      return (
        <div className="padding">
          <BackButton navlink="/summary" />
          {/* {this.renderErrorScreen(error)} */}
          <ApiErrorHandling error={error} />
        </div>
      );
    }

    // no request yet
    if (!loading && !eligibility) {
      this.renderBackScreen();
    }
    if (loading) return <Skeleton active className="padding" />;

    const { userAddSubs, userRecSubs, userOthSubs } = this.props;
    const userSubjects = userAddSubs.concat(userRecSubs).concat(userOthSubs);
    if (
      !userSubjects ||
      !(1 <= userSubjects.length && userSubjects.length <= 15)
    ) {
      this.renderBackScreen();
    }

    if (!this.props.eligibility)
      return (
        <div className="padding">
          <BackButton navlink="/summary" />
          <h1>
            Please complete at least one category to view HSC subject
            recommendations and eligibility
          </h1>
        </div>
      );

    let totalUnits = 0;

    eligibility.subjects.forEach((sub) => {
      totalUnits += sub.units;
    });
    const failureMessages = makeFailureMessages(eligibility);
    const subjectErrorOb = makeSubjectErrorOb(eligibility);

    return (
      <Row className="summary">
        <EmailModal
          modalVisible={this.state.modalVisible}
          closeModal={this.closeModal}
          summary={true}
        />
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <div className="padding">
            <BackButton navlink="/summary" />
            <h1>Your selected HSC subjects </h1>
            {eligibility.status === "ELIGIBLE" && (
              <div className="eligibility-results">
                Your selected HSC subject combination is{" "}
                <div className="inline-bold">eligible for an ATAR.</div>
              </div>
            )}
            {eligibility.status === "INELIGIBLE" && (
              <div className="eligibility-results">
                Your selected HSC subject combination is{" "}
                <div className="inline-bold">not eligible for an ATAR.</div>
              </div>
            )}

            {eligibility.status === "INDETERMINATE" && (
              <div className="eligibility-results">
                <div className="inline-bold">
                  <img
                    className="margin-right"
                    src={IconWarning}
                    alt="warning"
                    width="24px"
                    height="27.2px"
                  />
                  Go back to fix the errors in your subject combination and
                  re-submit your selection
                </div>
              </div>
            )}

            <Divider className="smaller-margin" />

            {renderFailureMessages(failureMessages)}
          </div>
        </Col>

        <Col sm={24} md={16} className="padding-top vertical-border-left">
          <div className="eligibility">
            <div className="padding-sides">
              <h2 className="title"> Subject summary</h2>
              <TableTopPopOver />
            </div>
            <List
              dataSource={eligibility.subjects}
              renderItem={(sub) => (
                <SubjectItem
                  {...sub}
                  handleToggleSubject={this.props.handleToggleSubject}
                  subjectItemSelected={true}
                  subjectError={subjectErrorOb[sub.subject_num]}
                  showSelect={false}
                />
              )}
            />
          </div>

          <div className="padding-sides eligibility-shaded">
            <div className="flex-space-between">
              <div className="subjects-result">
                <h2 className="title">
                  Total <span className="eligibility-normal">(for ATAR)</span>
                </h2>

                <p className="eligbility-normal">Non-ATAR subjects excluded</p>
                <p className="inline-bold">
                  Remember that you need to complete at least 12 units in Year
                  11.
                </p>
              </div>
              <div className="eligibility-total-units">{totalUnits} units</div>
            </div>
            <Divider className="eligibility-divider" />
          </div>

          <div className="width-100 eligibility-center-element">
            {/* stage 2  */}

            {eligibility.status === "ELIGIBLE" ? (
              <MasterButton
                type={BUTTON_TYPES.PRIMARY}
                text="Email Me My Summary"
                callback={this.showModal}
              />
            ) : (
              <NavLink to="/summary">
                <MasterButton
                  type={BUTTON_TYPES.ERROR}
                  text="Go Back to Fix Errors"
                />
              </NavLink>
            )}
          </div>

          <Divider />

          <div className="find-out-more padding-sides">
            <h1>Find out more</h1>
            {this.renderExternalLinks()}
          </div>
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = (store) => {
  const { eligibility, loading, error } = store.eligibilityReducer;
  const { userAddSubs } = store.pickAddSubReducer;
  const { userRecSubs } = store.pickRecSubReducer;
  const { userOthSubs } = store.pickOthSubReducer;
  return {
    eligibility,
    loading,
    error,
    userAddSubs,
    userRecSubs,
    userOthSubs,
  };
};

export default connect(mapStateToProps)(AtarEligible);
